import { Bell } from 'lucide-react'
import { forwardRef, useState, type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/use-mobile'
import { useNotifications } from '@/lib/notifications/use-notifications'
import { NotificationPanel } from './notification-panel'

const BellButton = forwardRef<
  HTMLButtonElement,
  ComponentProps<'button'> & { unreadCount: number; hasUnseenActivity: boolean }
>(function BellButton({ unreadCount, hasUnseenActivity, ...props }, ref) {
  const { t } = useTranslation('notifications')

  return (
    <button
      ref={ref}
      type="button"
      className="relative flex items-center justify-center h-8 w-8 rounded-lg hover:bg-accent transition-colors outline-none"
      aria-label={t('panel.bell')}
      {...props}
    >
      <Bell className="h-5 w-5 text-muted-foreground" />

      {/* Badge with count (unread notifications) */}
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}

      {/* Red dot (only unseen activities, no notification count) */}
      {unreadCount === 0 && hasUnseenActivity && (
        <span className="absolute top-0 right-0 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-background" />
      )}
    </button>
  )
})

/** Notifications: a popover on desktop, a bottom sheet on mobile. */
export function NotificationBell() {
  const { badge } = useNotifications()
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()
  const { t } = useTranslation('notifications')

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <BellButton unreadCount={badge.unreadCount} hasUnseenActivity={badge.hasUnseenActivity} />
        </DrawerTrigger>
        <DrawerContent className="max-h-[85vh]">
          <DrawerTitle className="sr-only">{t('panel.title')}</DrawerTitle>
          <DrawerDescription className="sr-only">{t('panel.description')}</DrawerDescription>
          <div className="min-h-0 flex-1 overflow-hidden pb-[env(safe-area-inset-bottom)]">
            <NotificationPanel onClose={() => setOpen(false)} />
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <BellButton unreadCount={badge.unreadCount} hasUnseenActivity={badge.hasUnseenActivity} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 max-h-[500px] flex flex-col" sideOffset={8}>
        <NotificationPanel onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}
